import React, { useCallback, useState, useEffect } from 'react'
import { AppRatingContext } from '.'
import { useUserSettings } from 'src/Hooks'
import { useHandleAppRatingUrlParameter } from '../hooks'

const APP_RATING_DELAY_MS = 3000

interface Props {
  children: React.ReactNode
  loggedIn: boolean
}

/**
 * Provider for the app rating modal to enable triggering the modal to open from anywhere on the app
 */
const AppRatingProvider: React.FC<Props> = ({ children, loggedIn }) => {
  const [openAppRatingTriggered, setOpenAppRatingTriggered] = useState(false)
  const [debouncedTriggered, setDebouncedTriggered] = useState(false)
  const [appRatingOpened, setAppRatingOpened] = useState(false)
  const { updateUserSettings } = useUserSettings()

  const openAppRating = useCallback((): void => {
    setAppRatingOpened(true)
  }, [])

  useHandleAppRatingUrlParameter({ loggedIn, openAppRating })

  /** Debounce only when transitioning from false -> true */
  useEffect(() => {
    if (openAppRatingTriggered) {
      const timeout = setTimeout(() => setDebouncedTriggered(true), APP_RATING_DELAY_MS)
      return () => clearTimeout(timeout)
    } else {
      setDebouncedTriggered(false)
    }
  }, [openAppRatingTriggered])

  const triggerAppRating = useCallback((): void => {
    setOpenAppRatingTriggered(true)
  }, [])

  const resetAppRatingTrigger = useCallback((): void => {
    setOpenAppRatingTriggered(false)
  }, [])

  const closeAppRating = useCallback((): void => {
    setAppRatingOpened(false)
  }, [])

  const resetAppRatingDates = useCallback((): void => {
    updateUserSettings({ remindMeLaterDate: '' })
    setOpenAppRatingTriggered(false)
    setAppRatingOpened(false)
  }, [updateUserSettings])

  const contextValue = React.useMemo(
    () => ({
      openAppRatingTriggered: debouncedTriggered,
      resetAppRatingTrigger,
      triggerAppRating,
      openAppRating,
      closeAppRating,
      appRatingOpened,
      resetAppRatingDates,
    }),
    [debouncedTriggered, resetAppRatingTrigger, triggerAppRating, openAppRating, closeAppRating, appRatingOpened, resetAppRatingDates]
  )

  return <AppRatingContext.Provider value={contextValue}>{children}</AppRatingContext.Provider>
}

export default AppRatingProvider
